/*
 * Filled, coloured glyphs drawn to match the Shift mockups (the lucide set is
 * outline-only). All decorative: aria-hidden, next to visible text.
 * Colours come from `currentColor` unless a glyph is multi-coloured.
 */

const base = (size, className) => ({
  width: size,
  height: size,
  viewBox: '0 0 48 48',
  className,
  'aria-hidden': true,
  focusable: 'false',
});

/* ---------- Stage glyphs (the four cycle nodes) ---------- */

export function MegaphoneGlyph({ size = 48, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M7 19.5c0-1.7 1.3-3 3-3h6.5L33 8v32l-16.5-8.5H10c-1.7 0-3-1.3-3-3Z" fill="currentColor" />
      <path d="M33 8v32l-16.5-8.5V16.5Z" fill="#000" opacity="0.12" />
      <path d="M13.5 31.5h6l2.6 8.7c.4 1.3-.6 2.6-2 2.6h-1.6c-1 0-1.8-.6-2-1.5Z" fill="currentColor" />
      <path d="M38.5 15.5l3.5-3.5M40 24h5M38.5 32.5l3.5 3.5" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function DocumentGlyph({ size = 48, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M12 4h17l11 11v26a3 3 0 0 1-3 3H12a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z" fill="currentColor" />
      <path d="M29 4v8.5a2.5 2.5 0 0 0 2.5 2.5H40Z" fill="#fff" opacity="0.45" />
      <path d="M16 24h16M16 30.5h16M16 37h10" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function ChatGlyph({ size = 48, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M24 5C13 5 4.5 12.6 4.5 22c0 4.4 1.9 8.4 5 11.4L7 43l10.3-4.6c2.1.6 4.4 1 6.7 1 11 0 19.5-7.6 19.5-17.4S35 5 24 5Z" fill="currentColor" />
      <circle cx="15.5" cy="22" r="2.8" fill="#fff" />
      <circle cx="24" cy="22" r="2.8" fill="#fff" />
      <circle cx="32.5" cy="22" r="2.8" fill="#fff" />
    </svg>
  );
}

export function GrowthGlyph({ size = 48, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="7" y="30" width="8" height="12" rx="2" fill="currentColor" />
      <rect x="20" y="24" width="8" height="18" rx="2" fill="currentColor" />
      <rect x="33" y="17" width="8" height="25" rx="2" fill="currentColor" />
      <path d="M6 24l10-9 6 5 14-13" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M30 6h9v9Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------- Attract feature glyphs ---------- */

export function SearchGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <circle cx="20" cy="20" r="13" fill="none" stroke="currentColor" strokeWidth="5" />
      <path d="M30 30l11 11" stroke="currentColor" strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

export function ShieldCheckGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M24 4l16 6v11c0 11-7 19.5-16 23C15 40.5 8 32 8 21V10Z" fill="currentColor" />
      <path d="M16.5 23.5l5.5 5.5 10-11" fill="none" stroke="#fff" strokeWidth="3.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CursorGlyph({ size = 34, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M9 6l30 13.5-12.5 4L22 37Z" fill="currentColor" stroke="currentColor" strokeWidth="3" strokeLinejoin="round" />
      <path d="M26 24l12 12" stroke="currentColor" strokeWidth="5.5" strokeLinecap="round" />
    </svg>
  );
}

/* ---------- Point-tile glyphs (Deliver, Counsel, Growth) ---------- */

export function PhoneGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="12" y="4" width="24" height="40" rx="5" fill="none" stroke="currentColor" strokeWidth="3.4" />
      <rect x="16.5" y="10" width="15" height="24" rx="2" fill="currentColor" opacity="0.35" />
      <circle cx="24" cy="38.5" r="2" fill="currentColor" />
    </svg>
  );
}

export function PdfGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M12 4h17l11 11v26a3 3 0 0 1-3 3H12a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3Z" fill="currentColor" />
      <path d="M29 4v8.5a2.5 2.5 0 0 0 2.5 2.5H40Z" fill="#fff" opacity="0.5" />
      <text x="24.5" y="35" fill="#fff" fontFamily="Inter, sans-serif" fontSize="11" fontWeight="800" textAnchor="middle">
        PDF
      </text>
    </svg>
  );
}

export function BulbGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M24 5a13 13 0 0 0-7.5 23.6c1.4 1 2.3 2.6 2.3 4.3V34h10.4v-1.1c0-1.7.9-3.3 2.3-4.3A13 13 0 0 0 24 5Z" fill="currentColor" opacity="0.3" />
      <path d="M24 5a13 13 0 0 0-7.5 23.6c1.4 1 2.3 2.6 2.3 4.3V34h10.4v-1.1c0-1.7.9-3.3 2.3-4.3A13 13 0 0 0 24 5Z" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinejoin="round" />
      <path d="M19 39.5h10M21 44h6" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
    </svg>
  );
}

export function ClipboardCheckGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="9" y="8" width="30" height="36" rx="4" fill="none" stroke="currentColor" strokeWidth="3.2" />
      <rect x="17" y="4" width="14" height="8" rx="2.5" fill="currentColor" />
      <path d="M17 27l5 5 9-10" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function CutleryGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M11 5v12a6 6 0 0 0 4 5.7V43a2.5 2.5 0 0 0 5 0V22.7a6 6 0 0 0 4-5.7V5h-3v11h-2V5h-3v11h-2V5Z" fill="currentColor" />
      <path d="M36 5c-4.5 0-7.5 6-7.5 14 0 3.3 1.5 5.6 4.5 6.4V43a2.5 2.5 0 0 0 5 0V5Z" fill="currentColor" />
    </svg>
  );
}

export function CalendarGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="6" y="9" width="36" height="34" rx="5" fill="none" stroke="currentColor" strokeWidth="3.2" />
      <path d="M6 14a5 5 0 0 1 5-5h26a5 5 0 0 1 5 5v5H6Z" fill="currentColor" />
      <path d="M15 5v8M33 5v8" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
      <rect x="13" y="26" width="7" height="6" rx="1.5" fill="currentColor" />
      <rect x="23" y="26" width="7" height="6" rx="1.5" fill="currentColor" opacity="0.4" />
    </svg>
  );
}

export function PeopleGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <circle cx="24" cy="13" r="6.5" fill="currentColor" />
      <circle cx="11" cy="18" r="5" fill="currentColor" opacity="0.7" />
      <circle cx="37" cy="18" r="5" fill="currentColor" opacity="0.7" />
      <path d="M12 41c0-8 5.4-14 12-14s12 6 12 14Z" fill="currentColor" />
      <path d="M2 40c0-6 3.8-10.5 9-10.5 1.7 0 3.2.4 4.5 1.2-2.6 2.6-4.2 6.2-4.5 9.3Z" fill="currentColor" opacity="0.7" />
      <path d="M46 40c0-6-3.8-10.5-9-10.5-1.7 0-3.2.4-4.5 1.2 2.6 2.6 4.2 6.2 4.5 9.3Z" fill="currentColor" opacity="0.7" />
    </svg>
  );
}

export function StarGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <path
        d="M24 4.5l5.9 12 13.1 1.9-9.5 9.3 2.3 13.1L24 34.6l-11.8 6.2 2.3-13.1L5 18.4l13.1-1.9Z"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PieGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <path d="M21 8a17 17 0 1 0 19 19H21Z" fill="currentColor" />
      <path d="M26 3a19 19 0 0 1 19 19H26Z" fill="currentColor" opacity="0.45" />
    </svg>
  );
}

export function ChartGlyph({ size = 26, className }) {
  return (
    <svg {...base(size, className)}>
      <rect x="7" y="30" width="7" height="12" rx="1.5" fill="currentColor" />
      <rect x="20" y="24" width="7" height="18" rx="1.5" fill="currentColor" />
      <rect x="33" y="18" width="7" height="24" rx="1.5" fill="currentColor" />
      <path d="M6 22l9-8 6 5 15-12" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M31 5.5h7.5V13Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

/* ---------- The patient (centre of the cycle) ---------- */

/*
 * A friendly flat bust in brand blues, holding a phone with their report
 * (a score ring and a few lines), a green tick for "report ready". Soft
 * gradients only; the bust is cropped to the round frame, the phone and the
 * tick sit just over its edge.
 */
export function PatientArt({ className }) {
  return (
    <svg viewBox="0 0 160 150" className={className} aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id="journey-pt-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#dce8ff" />
          <stop offset="1" stopColor="#bfd4ff" />
        </linearGradient>
        <linearGradient id="journey-pt-shirt" x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0" stopColor="#4d8cff" />
          <stop offset="1" stopColor="#1d5ce2" />
        </linearGradient>
        <linearGradient id="journey-pt-sleeve" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2f6ff2" />
          <stop offset="1" stopColor="#1449c4" />
        </linearGradient>
        <linearGradient id="journey-pt-hair" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#2a3d72" />
          <stop offset="1" stopColor="#121c3f" />
        </linearGradient>
        <linearGradient id="journey-pt-screen" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" />
          <stop offset="1" stopColor="#edf3ff" />
        </linearGradient>
        <clipPath id="journey-pt-frame">
          <circle cx="80" cy="76" r="62" />
        </clipPath>
      </defs>

      <circle cx="80" cy="76" r="62" fill="url(#journey-pt-bg)" />

      {/* Shoulders, neck and collar, cropped to the frame */}
      <g clipPath="url(#journey-pt-frame)">
        <path d="M14 152c0-37 26-60 66-60s66 23 66 60Z" fill="url(#journey-pt-shirt)" />
        <path d="M72 74h16v15c0 4.4-3.6 7-8 7s-8-2.6-8-7Z" fill="#e9b091" />
        <path d="M72 77q8 7.5 16 0v5q-8 6.5-16 0Z" fill="#d6957a" opacity="0.55" />
        <path
          d="M66.5 94l13.5 14.5 13.5-14.5c-3.4-1.4-8.4-2.2-13.5-2.2s-10.1.8-13.5 2.2Z"
          fill="#eef4ff"
        />
        <path d="M70.5 94l9.5 10 9.5-10" fill="none" stroke="#c8d9fb" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M128 108c9 8 10 24 2 38H104l12-28c3-6 6-9 12-10Z" fill="url(#journey-pt-sleeve)" />
      </g>

      {/* Head */}
      <circle cx="62" cy="62" r="4.4" fill="#e9b091" />
      <circle cx="98" cy="62" r="4.4" fill="#e9b091" />
      <ellipse cx="80" cy="60" rx="18" ry="20" fill="#f6c9aa" />
      <path
        d="M61.5 62C58.5 45 67 33 81 33c14 0 22.5 10 19 28-1.6-5.6-4.2-9.4-7.6-11.4C86 54 74 55.5 66 53.2c-2.4 2.6-4 5.4-4.5 8.8Z"
        fill="url(#journey-pt-hair)"
      />
      <path d="M73 39c5-2.4 11-2.6 16-.6" fill="none" stroke="#4a5f99" strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />
      <ellipse cx="73.2" cy="63" rx="1.9" ry="2.3" fill="#1b2a55" />
      <ellipse cx="86.8" cy="63" rx="1.9" ry="2.3" fill="#1b2a55" />
      <ellipse cx="69" cy="70" rx="3.2" ry="1.9" fill="#f29a8a" opacity="0.45" />
      <ellipse cx="91" cy="70" rx="3.2" ry="1.9" fill="#f29a8a" opacity="0.45" />
      <path d="M75.5 72q4.5 4 9 0" fill="none" stroke="#b9654a" strokeWidth="1.9" strokeLinecap="round" />

      {/* The phone with their report, held in one hand */}
      <g transform="rotate(-10 108 112)">
        <rect x="96" y="89" width="28" height="47" rx="6" fill="#0b2a7a" opacity="0.14" />
        <rect x="99" y="117" width="20" height="17" rx="8" fill="#e9b091" />
        <rect x="94" y="86" width="28" height="47" rx="6" fill="#15295e" />
        <rect x="97" y="89.5" width="22" height="40" rx="3.6" fill="url(#journey-pt-screen)" />
        <rect x="104" y="91.3" width="8" height="1.6" rx="0.8" fill="#15295e" />
        <circle cx="108" cy="103" r="5.6" fill="none" stroke="#dbe5f5" strokeWidth="2.4" />
        <path d="M108 97.4a5.6 5.6 0 1 1-5.6 5.6" fill="none" stroke="#22b573" strokeWidth="2.4" strokeLinecap="round" />
        <rect x="100.5" y="113" width="15" height="2.4" rx="1.2" fill="#1f6bff" />
        <rect x="100.5" y="118" width="11" height="2.4" rx="1.2" fill="#c9d8f5" />
        <rect x="100.5" y="123" width="13" height="2.4" rx="1.2" fill="#c9d8f5" />
        <rect x="90" y="112" width="8.5" height="17" rx="4.25" fill="#f6c9aa" />
        <path d="M92.5 116.5c1.6-.4 3.4-.4 4.4.2" fill="none" stroke="#e1a184" strokeWidth="1" strokeLinecap="round" />
      </g>

      {/* Report ready */}
      <g transform="translate(121 42)">
        <circle r="12" fill="#fff" stroke="#d3e1fc" strokeWidth="2" />
        <path
          d="M-5.2 0.6l3.4 3.4 7-7.4"
          fill="none"
          stroke="#22b573"
          strokeWidth="2.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </svg>
  );
}
